import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, Upload, ChevronLeft, ChevronRight, Trash2, X, CheckSquare } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import ImportModal from "@/components/ImportModal";
import { SECTORS, NEEDS, STATUSES, SOURCES, sectorLabel, needLabel, formatDateTime, statusMeta } from "@/lib/constants";
import { toast } from "sonner";

const PAGE_SIZE = 40;

function Dropdown({ label, value, onChange, options, testId, width = "min-w-[170px]" }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        className={`h-11 ${width} rounded-lg border px-4 text-sm flex items-center justify-between gap-2 transition-colors duration-150 ${
          value ? "border-slate-300 bg-white text-slate-900" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
        }`}
      >
        <span className="truncate">{selected ? selected.label : label}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" className="text-slate-400"><path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-2 min-w-[200px] rounded-lg border border-slate-200 bg-white shadow-sm py-1" data-testid={`${testId}-menu`}>
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              className="w-full text-left px-4 py-2 text-sm text-slate-500 hover:bg-slate-50"
              data-testid={`${testId}-clear`}
            >
              Tümü
            </button>
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); }}
                data-testid={`${testId}-opt-${o.value}`}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${value === o.value ? "text-orange-600 font-medium" : "text-slate-700"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function joinNotes(updates) {
  if (!updates || updates.length === 0) return "";
  return updates.map((u) => u.note).filter(Boolean).join(" - ");
}

function truncate(s, n = 90) {
  if (!s) return "";
  return s.length <= n ? s : s.slice(0, n).trimEnd() + "…";
}

function Pagination({ page, pageCount, onChange }) {
  if (pageCount <= 1) return null;
  const pages = [];
  const push = (v) => pages.push(v);
  const near = 1;
  const set = new Set([1, pageCount, page]);
  for (let i = -near; i <= near; i++) {
    if (page + i > 1 && page + i < pageCount) set.add(page + i);
  }
  const sorted = [...set].filter((n) => n >= 1 && n <= pageCount).sort((a, b) => a - b);
  let prev = 0;
  for (const n of sorted) {
    if (n - prev > 1) push("...");
    push(n);
    prev = n;
  }

  return (
    <div className="flex items-center gap-1 justify-end mt-6" data-testid="pagination">
      <button
        type="button"
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        data-testid="pagination-prev"
        className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white flex items-center justify-center transition-colors duration-150"
      >
        <ChevronLeft size={16} />
      </button>
      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`e${i}`} className="px-2 text-slate-400 text-sm">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            data-testid={`pagination-page-${p}`}
            className={`h-9 min-w-[36px] px-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
              p === page ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        disabled={page === pageCount}
        onClick={() => onChange(page + 1)}
        data-testid="pagination-next"
        className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white flex items-center justify-center transition-colors duration-150"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

export default function CallsList() {
  const [data, setData] = useState({ items: [], total: 0, page_count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sector, setSector] = useState("");
  const [status, setStatus] = useState("");
  const [need, setNeed] = useState("");
  const [source, setSource] = useState("");
  const [location, setLocation] = useState("");
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // selection
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // reset page when filters change
  const filters = useMemo(() => ({ search, sector, status, need, source, location }), [search, sector, status, need, source, location]);
  useEffect(() => { setPage(1); }, [filters]);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      try {
        const { data: resp } = await api.get("/calls", { params });
        setData(resp);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [filters, page, reloadKey]);

  // reset selection when leaving select mode / when list changes
  useEffect(() => { if (!selectMode) setSelected(new Set()); }, [selectMode]);

  const items = data.items || [];
  const total = data.total || 0;
  const pageCount = data.page_count || 0;
  const hasFilters = search || sector || status || need || source || location;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const nxt = new Set(prev);
      if (nxt.has(id)) nxt.delete(id);
      else nxt.add(id);
      return nxt;
    });
  };

  const allOnPageSelected = items.length > 0 && items.every((c) => selected.has(c.id));
  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const nxt = new Set(prev);
      if (allOnPageSelected) items.forEach((c) => nxt.delete(c.id));
      else items.forEach((c) => nxt.add(c.id));
      return nxt;
    });
  };

  const doBulkStatus = async (newStatus) => {
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/calls/bulk/status", { ids: [...selected], status: newStatus });
      toast.success(`${res.updated} kaydın durumu güncellendi`);
      setBulkStatusOpen(false);
      setSelected(new Set());
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBulkBusy(false);
    }
  };

  const doBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/calls/bulk/delete", { ids: [...selected] });
      toast.success(`${res.deleted} kayıt silindi`);
      setConfirmBulkDelete(false);
      setSelected(new Set());
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBulkBusy(false);
    }
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <Layout>
      <div className="mb-10 flex justify-between items-end border-b border-slate-100 pb-6" data-testid="calls-header">
        <div>
          <h1 className="text-4xl tracking-tight font-bold text-slate-900">Tüm Çağrılar</h1>
          <p className="text-sm text-slate-500 mt-2">Kayıtlarınızı arayın, filtreleyin ve inceleyin.</p>
        </div>
        <Link
          to="/calls/new"
          data-testid="calls-new-btn"
          className="rounded-full bg-slate-900 text-white hover:bg-slate-800 px-5 py-2.5 font-medium text-sm transition-colors duration-150"
        >
          Yeni Çağrı Ekle
        </Link>
      </div>

      <div className="flex items-center justify-end gap-2 mb-4">
        <button
          type="button"
          onClick={() => setSelectMode((v) => !v)}
          data-testid="select-mode-toggle"
          className={`rounded-full border px-4 py-2 font-medium text-sm transition-colors duration-150 flex items-center gap-2 ${
            selectMode
              ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-800"
              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
        >
          <CheckSquare size={14} /> {selectMode ? "Seçim Modu Kapalı" : "Seç"}
        </button>
        <button
          type="button"
          onClick={() => setImportOpen(true)}
          data-testid="import-open-btn"
          className="rounded-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 px-4 py-2 font-medium text-sm transition-colors duration-150 flex items-center gap-2"
        >
          <Upload size={14} /> CSV İçe Aktar
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Marka, yetkili, telefon veya not içinde ara..."
            data-testid="search-input"
            className="w-full h-11 rounded-lg border border-slate-200 bg-white pl-10 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 placeholder:text-slate-400"
          />
        </div>
        <Dropdown label="Sektör" value={sector} onChange={setSector} options={SECTORS} testId="filter-sector" />
        <Dropdown label="Durum" value={status} onChange={setStatus} options={STATUSES} testId="filter-status" />
        <Dropdown label="Hizmet Türü" value={need} onChange={setNeed} options={NEEDS} testId="filter-need" />
        <Dropdown label="Kaynak" value={source} onChange={setSource} options={SOURCES} testId="filter-source" width="min-w-[180px]" />
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Lokasyon"
          data-testid="filter-location"
          className="h-11 w-[160px] rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 placeholder:text-slate-400"
        />
      </div>

      {selectMode && selected.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-slate-900 bg-slate-900 text-white px-4 py-3" data-testid="bulk-bar">
          <div className="text-sm font-medium">{selected.size} kayıt seçildi</div>
          <div className="flex items-center gap-2 relative">
            <button
              type="button"
              onClick={() => setBulkStatusOpen((v) => !v)}
              data-testid="bulk-status-btn"
              className="rounded-full bg-white text-slate-900 hover:bg-slate-100 px-4 py-1.5 text-sm font-medium transition-colors duration-150"
            >
              Durum Değiştir
            </button>
            {bulkStatusOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setBulkStatusOpen(false)} />
                <div className="absolute right-[110px] top-full mt-2 z-20 rounded-lg border border-slate-200 bg-white shadow-md py-1 min-w-[180px]" data-testid="bulk-status-menu">
                  {STATUSES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => doBulkStatus(s.value)}
                      disabled={bulkBusy}
                      data-testid={`bulk-status-${s.value}`}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <span className={`status-dot ${s.dot}`} />
                      {s.label}
                    </button>
                  ))}
                </div>
              </>
            )}
            <button
              type="button"
              onClick={() => setConfirmBulkDelete(true)}
              data-testid="bulk-delete-btn"
              className="rounded-full bg-red-500 text-white hover:bg-red-600 px-4 py-1.5 text-sm font-medium transition-colors duration-150 flex items-center gap-1.5"
            >
              <Trash2 size={13} /> Sil
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              data-testid="bulk-clear"
              className="text-slate-300 hover:text-white p-1"
              title="Seçimi temizle"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid="calls-table-wrap">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="calls-loading">Yükleniyor...</div>
        ) : items.length === 0 ? (
          <div className="p-14 text-center" data-testid="calls-empty">
            <div className="mx-auto w-14 h-14 rounded-full border border-dashed border-slate-300 flex items-center justify-center mb-4">
              <Filter size={20} className="text-slate-400" />
            </div>
            <div className="text-sm text-slate-500 mb-1">Sonuç bulunamadı</div>
            <div className="text-xs text-slate-400">{hasFilters ? "Filtreleri değiştirmeyi deneyin." : "Henüz kayıt eklemediniz."}</div>
          </div>
        ) : (
          <table className="w-full table-fixed">
            <colgroup>
              {selectMode && <col style={{ width: "44px" }} />}
              <col style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead>
              <tr className="border-b border-slate-200">
                {selectMode && (
                  <th className="text-left py-4 px-4">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleAllOnPage}
                      data-testid="select-all-page"
                      className="rounded border-slate-300"
                    />
                  </th>
                )}
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Marka Adı</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Yetkili</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Telefon</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sektör</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">İhtiyaç</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Notlar</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Güncelleme</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Durum</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const joined = joinNotes(c.updates);
                const isSelected = selected.has(c.id);
                return (
                  <tr
                    key={c.id}
                    className={`border-b border-slate-100 last:border-none transition-colors ${
                      isSelected ? "bg-slate-50" : "hover:bg-slate-50"
                    } ${!selectMode ? "cursor-pointer" : ""}`}
                    onClick={(e) => {
                      if (selectMode) {
                        toggleOne(c.id);
                      } else {
                        window.location.href = `/calls/${c.id}`;
                      }
                    }}
                    data-testid={`call-row-${c.id}`}
                  >
                    {selectMode && (
                      <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleOne(c.id)}
                          data-testid={`row-check-${c.id}`}
                          className="rounded border-slate-300"
                        />
                      </td>
                    )}
                    <td className="py-4 px-6 text-sm font-medium text-slate-900 truncate">{c.brand_name || <span className="text-slate-300">—</span>}</td>
                    <td className="py-4 px-6 text-sm text-slate-700 truncate">{c.contact_name || <span className="text-slate-300">—</span>}</td>
                    <td className="py-4 px-6 text-sm text-slate-700 tabular-nums truncate">{c.phone}</td>
                    <td className="py-4 px-6 text-sm text-slate-700 truncate">{sectorLabel(c.sector, c.sector_other)}</td>
                    <td className="py-4 px-6 text-sm text-slate-700">
                      {c.needs?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {c.needs.slice(0, 2).map((n) => (
                            <span key={n} className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs">{needLabel(n)}</span>
                          ))}
                          {c.needs.length > 2 && <span className="text-xs text-slate-400">+{c.needs.length - 2}</span>}
                        </div>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm text-slate-600" title={joined || "Not yok"} data-testid={`notes-cell-${c.id}`}>
                      {joined ? (
                        <span className="line-clamp-2 leading-snug">{truncate(joined, 90)}</span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm text-slate-500 tabular-nums truncate">{formatDateTime(c.updated_at)}</td>
                    <td className="py-4 px-6"><StatusBadge status={c.current_status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && total > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-slate-500" data-testid="calls-count">
            {rangeStart}-{rangeEnd} / <span className="font-medium text-slate-700">{total}</span> kayıt
            {selected.size > 0 && <span className="ml-3 text-slate-400">· {selected.size} seçili</span>}
          </div>
          <Pagination page={data.page || page} pageCount={pageCount} onChange={setPage} />
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => setReloadKey((k) => k + 1)}
      />

      {confirmBulkDelete && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center px-6" data-testid="bulk-delete-modal">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm max-w-[440px] w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">{selected.size} kayıt silinecek</h3>
            <p className="text-sm text-slate-500 mb-6">Bu işlem geri alınamaz. Seçili {selected.size} kayıt ve tüm güncelleme geçmişleri kalıcı olarak silinecek.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmBulkDelete(false)}
                data-testid="bulk-delete-cancel"
                className="rounded-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 px-5 py-2 text-sm font-medium transition-colors duration-150"
              >
                Vazgeç
              </button>
              <button
                onClick={doBulkDelete}
                disabled={bulkBusy}
                data-testid="bulk-delete-confirm"
                className="rounded-full bg-red-600 text-white hover:bg-red-700 px-5 py-2 text-sm font-medium transition-colors duration-150 disabled:opacity-50"
              >
                {bulkBusy ? "Siliniyor..." : `Evet, ${selected.size} kaydı sil`}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
